import React, { useEffect, useState, useRef } from 'react';
import { fetchSessions, fetchChatHistory, newChat, removeSession, renameSession, duplicateSession, sendMessage, messageAction } from './realApi';
import SettingsModal from './SettingsModal';
import { FaBan, FaTrash, FaVolumeUp } from "react-icons/fa";
import './ChatGPTLayout.css';
import { BACKEND_ENDPOINT } from './config/authConfig';

import ReactMarkdown from "react-markdown";
import { FaCodeFork, FaCopy } from 'react-icons/fa6';
//import { Editor } from "@toast-ui/react-editor";
//import "@toast-ui/editor/dist/toastui-editor.css";

//import ReactQuill from "react-quill";
//import "react-quill/dist/quill.snow.css"; // Quill styles

const WEBSOCKET_URL = 'ws://localhost:8000/ws'
//const BACKEND_ENDPOINT = 'http://localhost:8000';

export default function ChatGPTLayout() {
  // SIDEBAR & SESSION STATE
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingName, setEditingName] = useState('');

  // CHAT STATE
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');

  //Scroll
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]); // Runs whenever messages change

  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      const lineHeight = 24; // Approximate px height of one line (adjust if needed)
      const maxHeight = lineHeight * 10; // max 10 lines
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, maxHeight)}px`;
    }
  }, [inputText]);

  // Additional Options State
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isDeepResearch, setIsDeepResearch] = useState(false);
  const [selectedModel, setSelectedModel] = useState('GPT-4');
  const [chatMode, setChatMode] = useState('Chat');
  const [isTemporaryChat, setIsTemporaryChat] = useState(false);

  // AUDIO STREAMING (LISTENING) STATE
  const [isListening, setIsListening] = useState(false);
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  // CONTINUOUS SCREEN CAPTURE STATE
  const [isCapturing, setIsCapturing] = useState(false);
  const screenshotIntervalRef = useRef(null);
  const [screenshotUrls, setScreenshotUrls] = useState([]);
  const wsScreenshotRef = useRef(null);
  // Hidden video element ref for continuous capture
  const captureVideoRef = useRef(null);

  // SETTINGS STATE (Popup Modal)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedTTSVoice, setSelectedTTSVoice] = useState('Google UK English Female');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [isTTSEnabled, setIsTTSEnabled] = useState(false);

  useEffect(() => {
    loadSessions();
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  async function loadSessions() {
    const data = await fetchSessions();
    setSessions(data);
    if (data.length > 0 && !selectedSession) {
      handleSessionSelect(data[0].id);
    }
  }

  function loadVoices() {
    const voices = window.speechSynthesis.getVoices();
    setAvailableVoices(voices);
    if (voices.length > 0 && !selectedTTSVoice) {
      setSelectedTTSVoice(voices[0].name);
    }
  }

  async function handleSessionSelect(sessionId) {
    setSelectedSession(sessionId);
    const history = await fetchChatHistory(sessionId);
    setMessages(history);
  }

  async function handleNewChat() {
    const newSession = await newChat();
    await loadSessions();
    handleSessionSelect(newSession.id);
  }

  async function handleDeleteSession(sessionId) {
    const confirmed = window.confirm('Are you sure you want to delete this session?');
    if (!confirmed) return;
    
    await removeSession(sessionId);
    await loadSessions();
    if (selectedSession === sessionId) {
      if (sessions.length > 1) {
        const newActive = sessions.find((s) => s.id !== sessionId);
        if (newActive) handleSessionSelect(newActive.id);
      } else {
        setSelectedSession(null);
        setMessages([]);
      }
    }
  }

  async function handleRenameSession(sessionId, newName) {
    const response = await renameSession(sessionId, newName);
    if (!response.ok) throw new Error('Failed to rename session');
    await loadSessions();
  }

  async function handleDuplicateSession(sessionId) {
    const response = await duplicateSession(sessionId);
    if (!response.ok) throw new Error('Failed to duplicate session');
    await loadSessions();
    handleSessionSelect(response.newSessionId);
  }

  async function handleSendMessage() {
    if (!inputText.trim() || !selectedSession) return;

    const userMessage = {uid: '~temp', role: 'user', content: inputText }; // Ensure it matches API format
    console.log("userMessage:", userMessage);
    setMessages(prev => [...prev, userMessage]); // Add user message instantly
  
    const options = {
      search: isSearchMode,
      deepResearch: isDeepResearch,
      model: selectedModel,
      chatMode,
      temporary: isTemporaryChat,
    };
  
    try {
      const data = await sendMessage(selectedSession, inputText, options);

      const userMessage = {
        uid: data[0].uid,
        role: 'user',
        content: data[0].text,
        sources: data[0].sources
      };

      const aiMessage = {
        uid: data[1].uid,
        role: 'assistant',
        content: data[1].text,
        sources: data[1].sources
      };
      
      //setMessages(prev => [...prev, userMessage, aiMessage]); // Update state with AI response
      setMessages(prev => {
        //Remove the temp message
        const filtered = prev.filter(msg => msg.uid !== '~temp');
        return [...filtered, userMessage, aiMessage];
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  
    setInputText(''); // Clear input field after sending
  }

  const readTextTTS = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = availableVoices.find((v) => v.name === selectedTTSVoice);
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
  };
  
  const handleSpeakMessage = (text) => {
    // Use the existing TTS function
    readTextTTS(text);
  };

  const handleMessageAction = async (uid, action) => {
    const response = await messageAction(selectedSession, uid, action);
    return response;
  };

  const handleForkMessage = async (uid) => {
    const fork = await handleMessageAction(uid, 'fork');
    console.log(fork)
    await loadSessions();
    handleSessionSelect(fork.id);
  };

  const toggleIgnoreMessage = (uid) => {
    setMessages(prev =>
      prev.map((msg, idx) => {
        const id = msg.uid || idx;
        if (id === uid) {
          const currentIgnore = msg.options?.ignore ?? false;
          return {
            ...msg,
            options: {
              ...(msg.options || {}),
              ignore: !currentIgnore, 
            }
          };
        }
        return msg;
      })
    );
  
    // Notify the server
    return handleMessageAction(uid, 'ignore');
  };

  const handleDeleteMessage = (uid) => {
    const confirmed = window.confirm('Are you sure you want to delete this message?');
    if (!confirmed) return;
    
    // Optionally call the server API:
    handleMessageAction(uid, 'delete');

    // Remove the message from local state
    setMessages(prev => prev.filter((msg, idx) => (msg.uid || idx) !== uid));
  }

  // --- AUDIO STREAMING (LISTENING) FUNCTIONS ---
  const startListening = async () => {
    try {
      const ws = new WebSocket(`${WEBSOCKET_URL}/transcribe`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        console.log("Transcription received:", event.data);
      };
      ws.onclose = () => {
        setIsListening(false);
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        }
      };
      mediaRecorder.start(1000);
      setIsListening(true);
    } catch (error) {
      console.error("Error starting listening:", error);
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsListening(false);
  };

  // --- CONTINUOUS SCREEN CAPTURE FUNCTIONS ---
  // Use one continuous video stream and capture a frame every second.
  const startCapturingScreens = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      setIsCapturing(true);
      if (captureVideoRef.current) {
        captureVideoRef.current.srcObject = stream;
        await captureVideoRef.current.play();
      }
      screenshotIntervalRef.current = setInterval(() => {
        captureFrame();
      }, 1000);
    } catch (error) {
      console.error("Error starting continuous capture:", error);
    }
  };
  
  const captureFrame = () => {
    if (captureVideoRef.current) {
      const video = captureVideoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(async (blob) => {
        if (blob) {
          const formData = new FormData();
          formData.append("file", blob, "screenshot.jpg");
          try {
            //TODO: Update to axios
            await fetch(`${BACKEND_ENDPOINT}/api/screen-capture?sessionId=${selectedSession}`, {
              method: "POST",
              body: formData,
            });
          } catch (error) {
            console.error("Error sending captured frame:", error);
          }
        }
      }, "image/jpeg");
    }
  };

  const stopCapturingScreens = () => {
    if (screenshotIntervalRef.current) {
      clearInterval(screenshotIntervalRef.current);
      screenshotIntervalRef.current = null;
    }
    if (captureVideoRef.current && captureVideoRef.current.srcObject) {
      const tracks = captureVideoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      captureVideoRef.current.srcObject = null;
    }
    setIsCapturing(false);
  };

  // --- SETTINGS DIALOG (Popup Modal) ---
  const saveSettings = async () => {
    try {
      const settings = {
        ttsVoice: selectedTTSVoice,
      };
      const response = await fetch(`${BACKEND_ENDPOINT}/api/save-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      const data = await response.json();
      console.log(data.detail);
      setIsSettingsOpen(false);
    } catch (error) {
      console.error("Error saving settings:", error);
    }
  };

  const toggleSettings = () => {
    setIsSettingsOpen((prev) => !prev);
  };

  // --- WebSocket for Live Screenshot Updates ---
  useEffect(() => {
    if (isCapturing && selectedSession) {
      wsScreenshotRef.current = new WebSocket(`${WEBSOCKET_URL}/screenshot-stream?sessionId=${selectedSession}`);
      wsScreenshotRef.current.onmessage = (event) => {
        setScreenshotUrls([event.data]); // Only the latest screenshot URL
      };
      return () => {
        if (wsScreenshotRef.current) wsScreenshotRef.current.close();
      };
    }
  }, [isCapturing, selectedSession]);

  // --- Toggle Sidebar ---
  function toggleSidebar() {
    setIsSidebarOpen((prev) => !prev);
  }

  return (
    <div className="chatgpt-layout">
      {/* SIDEBAR */}
      <aside className={`chat-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <button className="toggle-btn" onClick={toggleSidebar}>
            {isSidebarOpen ? '❮' : '❯'}
		  </button>
		  {isSidebarOpen && (
			<button className="new-chat-btn" onClick={handleNewChat}>
			  New Chat
			</button>
		  )}
	
        </div>
        <ul className="sidebar-list">
          {sessions.map((session) => (
		  
			<li
			  key={session.id}
			  className={`sidebar-item ${session.id === selectedSession ? 'active' : ''}`}
			>
			  <span className="icon">💬</span>
        <div 
  className="session-details" 
  onClick={() => {
    if (editingSessionId !== session.id) {
      handleSessionSelect(session.id);
    }
  }}
>
				{editingSessionId === session.id ? (
          <input
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onBlur={() => {
              handleRenameSession(session.id, editingName);
              setEditingSessionId(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleRenameSession(session.id, editingName);
                setEditingSessionId(null);
              }
            }}
            autoFocus
          />

				) : (
				  <span
					className="label"
          title={session.name}
					onDoubleClick={(e) => {
            e.stopPropagation(); // Prevent triggering onClick while double-clicking
					  setEditingSessionId(session.id);
					  setEditingName(session.name);
					}}
				  >
					{session.name}
				  </span>
				)}
			  </div>
			  {isSidebarOpen && (
          <div>
				<button
				  className="mini-delete-session-btn"
				  onClick={(e) => {
            e.stopPropagation();
            handleDeleteSession(session.id);
				  }}
				  title="Delete Session"
				>
				  <FaTrash style={{ fontSize: "10px" }} />
				</button>
        <button
				  className="mini-duplicate-session-btn"
				  onClick={(e) => {
            e.stopPropagation();
            handleDuplicateSession(session.id);
				  }}
				  title="Duplicate Session"
				>
				  <FaCopy style={{ fontSize: "10px" }} />
				</button>
        </div>
			  )}
			</li>

			
          ))}
        </ul>
        <div className="sidebar-footer">
          <button className="settings-icon-btn" onClick={toggleSettings}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z" />
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.12c-1.64-.668-3.434.889-2.766 2.528l.12.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.12.292c-.668 1.64.889 3.434 2.528 2.766l.292-.12a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.12c1.64.668 3.434-.889 2.766-2.528l-.12-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.12-.292c.668-1.64-.889-3.434-2.528-2.766l-.292.12a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.12c.764-.31 1.6.414 1.289 1.178l-.12.292a1.873 1.873 0 0 0 1.115 2.693l.319.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.12.291c.31.764-.414 1.6-1.178 1.289l-.292-.12a1.873 1.873 0 0 0-2.693 1.115l-.094.319c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.693-1.115l-.291.12c-.764.31-1.6-.414-1.289-1.178l.12-.292A1.873 1.873 0 0 0 2.9 8.94l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 4.334 5.38l-.12-.291c-.31-.764.414-1.6 1.178-1.289l.292.12a1.873 1.873 0 0 0 2.693-1.115l.094-.319z" />
            </svg>
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="main-content">
        {/* CHAT WINDOW */}
		
		    <div className="chat-window">
          {messages.map((msg, idx) => (
            <div key={msg.uid || idx} dummy={msg.uid} className={`message-bubble ${msg.role === 'user' ? 'user' : 'ai'} ${msg.options?.ignore ? 'ignored' : ''}`}>
              <div className="message-content">
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              </div>

              {/* Sources Section */}
              {msg.sources && msg.sources.length > 0 && (
                <div className="message-sources">
                  {msg.sources.map((source, sourceIdx) => (
                    <a 
                      key={sourceIdx} 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="source-button"
                    >
                      {source.title}
                    </a>
                  ))}
                </div>
              )}

              <div className="message-actions">
                <button onClick={() => toggleIgnoreMessage(msg.uid)} title={msg.ignored ? 'Unignore' : 'Ignore'}>
                  <FaBan style={{ fontSize: "14px" }} />
                </button>
                <button onClick={() => handleDeleteMessage(msg.uid)} title="Delete">
                  <FaTrash style={{ fontSize: "14px" }} />
                </button>
                <button onClick={() => handleForkMessage(msg.uid)} title="Fork">
                  <FaCodeFork style={{ fontSize: "14px" }} />
                </button>
                <button onClick={() => handleSpeakMessage(msg.content)} title="Speak">
                  <FaVolumeUp style={{ fontSize: "14px" }} />
                </button>
              </div>
              <div ref={messagesEndRef}></div>
            </div>
          ))}
        </div>

        {/* OPTIONS PANEL */}
        <div className="options-panel">
          <label>
            <input
              type="checkbox"
              checked={isSearchMode}
              onChange={(e) => setIsSearchMode(e.target.checked)}
            />
            Search
          </label>
          <label>
            <input
              type="checkbox"
              checked={isDeepResearch}
              onChange={(e) => setIsDeepResearch(e.target.checked)}
              disabled="true"
            />
            Deep Research
          </label>
          <label>
            Model:
            <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} disabled="true">
              <option value="genai">Gemini</option>
              <option value="google">Google</option>
            </select>
          </label>
          <label>
            Chat Mode:
            <select value={chatMode} onChange={(e) => setChatMode(e.target.value)} disabled="true">
              <option value="Chat">Chat</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label>
            Temporary:
            <input
              disabled="true"
              type="checkbox"
              checked={isTemporaryChat}
              onChange={(e) => setIsTemporaryChat(e.target.checked)}
            />
          </label>
        </div>

        {/* INPUT BAR */}
        <div className="input-bar">
        <textarea className="input-bar-textarea"
            ref={inputRef}
            placeholder="Type your message...
shift + enter for new line"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.shiftKey) { //shiftKey //ctrlKey
                setInputText(inputText + "\n"); // Adds a new line when pressing Enter
                e.preventDefault();
              } else if (e.key === 'Enter') {
                handleSendMessage(); //Submit
                setInputText(""); // Clear the input field after submission
                e.preventDefault();
              }
            }}
            rows={3} // Initial size
            style={{
              width: "100%",
              resize: "none",
              overflow: "hidden", // prevent scrollbar flickering
              lineHeight: "24px", // match lineHeight from JS
              overflowY: "scroll"
            }}
          />

          <div alt="ReactQuill"
            theme="snow" // WYSIWYG with toolbar
            value={editingName}
            onChange={setInputText}
            placeholder="Type your message..."
            modules={{
              toolbar: [
                ["bold", "italic", "underline"], // Basic text styles
                [{ list: "ordered" }, { list: "bullet" }], // Lists
                ["link"], // Links
              ],
            }}
            formats={["bold", "italic", "underline", "list", "bullet", "link"]}
          />
          <div className="input-bar-buttons">
            <button onClick={handleSendMessage}>Send</button>
            <button onClick={isListening ? stopListening : startListening}>
              {isListening ? "Stop Listening" : "Start Listening"}
            </button>
            <button onClick={isCapturing ? stopCapturingScreens : startCapturingScreens}>
              {isCapturing ? "Stop Capturing" : "Capture Screen"}
            </button>
          </div>
        </div>

        {/* Hidden video element for continuous capture */}
        <video ref={captureVideoRef} style={{ display: 'none' }} />

        {/* LIVE SCREENSHOT DISPLAY - Only show the last screenshot */}
        {isCapturing && screenshotUrls.length > 0 && (
          <div className="screenshot-container">
            <h3>Live Screenshot</h3>
            <img src={screenshotUrls[0]} alt="Live Screenshot" style={{ width: "100px", margin: "5px" }} />
          </div>
        )}

        {/* SETTINGS DIALOG (Popup Modal) */}
        {isSettingsOpen && (
          <SettingsModal
            availableVoices={availableVoices}
            selectedTTSVoice={selectedTTSVoice}
            setSelectedTTSVoice={setSelectedTTSVoice}
            isTTSEnabled={isTTSEnabled}
            setIsTTSEnabled={setIsTTSEnabled}
            saveSettings={saveSettings}
            toggleSettings={toggleSettings}
          />
        )}
      </div>
    </div>
  );
}
